import React, { useState, useEffect, useCallback } from 'react';
import { connect } from 'react-redux';
import { Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Dropdown, Button, Row, Col } from 'react-bootstrap';

const AISettings = ({ auth, platform_league_id }) => {
    const location = useLocation();
    const [currentRoastLevel, setCurrentRoastLevel] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    // Roast level options (matching onboarding component)
    const roastLevels = [
        {
            id: 'participation_trophy',
            emoji: '🏆',
            title: 'Participation Trophy',
            description: 'A lighthearted roast where even the losers get participation trophies'
        },
        {
            id: 'sunburn',
            emoji: '🔥',
            title: 'Sunburn',
            description: 'Roasting with a sting that lasts'
        },
        {
            id: 'red_band',
            emoji: '🤬',
            title: 'Red Band (coming soon)',
            description: 'Profanity-laced roasts that would make your group chat blush'
        }
    ];

    const fetchCurrentRoastLevel = useCallback(async () => {
        try {
            const response = await axios.get(`/api/get/roast-level/${platform_league_id}`);
            if (response.data.success) {
                setCurrentRoastLevel(response.data.roast_level || '');
            }
        } catch (error) {
            console.error('Error fetching roast level:', error);
            setError('Failed to load roast level');
        }
    }, [platform_league_id]);

    // Fetch current roast level on component mount
    useEffect(() => {
        if (platform_league_id) {
            fetchCurrentRoastLevel();
        }
    }, [platform_league_id, fetchCurrentRoastLevel]);

    const handleRoastLevelChange = async (roastLevelId) => {
        if (!roastLevelId || roastLevelId === currentRoastLevel) {
            return;
        }

        setIsLoading(true);
        setError(null);

        try {
            const response = await axios.post('/api/post/roast-level', {
                roast_level: roastLevelId,
                platform_league_id: platform_league_id
            });

            if (response.data.success) {
                setCurrentRoastLevel(roastLevelId);
            } else {
                throw new Error(response.data.error || 'Failed to update roast level');
            }
        } catch (error) {
            console.error('Error updating roast level:', error);
            setError('Failed to update roast level');
        } finally {
            setIsLoading(false);
        }
    };

    const getCurrentRoastLevelDisplay = () => {
        const level = roastLevels.find(l => l.id === currentRoastLevel);
        if (!level) return 'Select Roast Level';
        return `${level.emoji} ${level.title}`;
    };

    // Check if we're on league context or weekly context pages
    const isLeagueContextActive = location.pathname === '/league-context';
    const isWeeklyContextActive = location.pathname === '/weekly-context';

    // Show loading state if no platform_league_id yet
    if (!platform_league_id) {
        return (
            <div className="ai-settings-container">
                <Row className="g-2">
                    <Col xs={12}>
                        <div className="text-center py-3">
                            <div className="spinner-border spinner-border-sm text-primary me-2" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                            <span className="text-muted">Loading settings...</span>
                        </div>
                    </Col>
                </Row>
            </div>
        );
    }

    return (
        <div className="ai-settings-container">
            <Row className="g-2">
                {/* Roast Level Dropdown */}
                <Col xs={12} sm={6} md={4}>
                    <Dropdown>
                        <Dropdown.Toggle 
                            variant="outline-light" 
                            className="ai-settings-dropdown-toggle w-100"
                            disabled={isLoading}
                        >
                            {isLoading ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                    Updating...
                                </>
                            ) : (
                                getCurrentRoastLevelDisplay()
                            )}
                        </Dropdown.Toggle>
                        <Dropdown.Menu className="ai-settings-dropdown-menu">
                            {roastLevels.map(level => (
                                <Dropdown.Item 
                                    key={level.id}
                                    className={currentRoastLevel === level.id ? 'active' : ''}
                                    disabled={level.id === 'red_band'}
                                    onClick={() => {
                                        if (level.id !== 'red_band') {
                                            handleRoastLevelChange(level.id);
                                        }
                                    }}
                                >
                                    <div className="roast-level-option">
                                        <span className="roast-level-emoji">{level.emoji}</span>
                                        <div className="roast-level-text">
                                            <div className="roast-level-title">{level.title}</div>
                                            <div className="roast-level-description">{level.description}</div>
                                        </div>
                                    </div>
                                </Dropdown.Item>
                            ))}
                        </Dropdown.Menu>
                    </Dropdown>
                </Col>

                {/* League Context Button */}
                <Col xs={6} sm={3} md={4}>
                    <Button 
                        as={Link}
                        to="/league-context"
                        variant={isLeagueContextActive ? "primary" : "outline-light"}
                        className={`ai-settings-context-btn w-100 ${isLeagueContextActive ? 'active' : ''}`}
                    >
                        <i className="bi bi-pencil-square me-2"></i>
                        League Context
                    </Button>
                </Col>

                {/* Weekly Context Button */}
                <Col xs={6} sm={3} md={4}>
                    <Button 
                        as={Link}
                        to="/weekly-context"
                        variant={isWeeklyContextActive ? "primary" : "outline-light"}
                        className={`ai-settings-context-btn w-100 ${isWeeklyContextActive ? 'active' : ''}`}
                    >
                        <i className="bi bi-pencil-square me-2"></i>
                        Weekly Context
                    </Button>
                </Col>
            </Row>

            {/* Error Message */}
            {error && (
                <Row className="mt-2">
                    <Col>
                        <div className="ai-settings-error">
                            <i className="bi bi-exclamation-triangle me-2"></i>
                            {error}
                        </div>
                    </Col>
                </Row>
            )}
        </div>
    );
};

function mapStateToProps({auth}) {
    return { auth };
}

export default connect(mapStateToProps)(AISettings);
